import { useMemo, useState } from 'react'
import { Modal, Input, Textarea, Button } from '@/components/ui'

const FORMSPREE_ENDPOINT = 'https://formspree.io/f/mqeorznr'

// Working-hours slots offered for every available day (GMT). Kept on the front
// end only — the actual scheduling is confirmed by the team out of band.
const TIME_SLOTS = [
  '09:00',
  '09:30',
  '10:00',
  '10:30',
  '11:00',
  '11:30',
  '13:00',
  '13:30',
  '14:00',
  '14:30',
  '15:00',
  '15:30',
  '16:00',
  '16:30',
]

const WEEKDAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
const MONTH_LABELS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
]

function startOfDay(date: Date): Date {
  const d = new Date(date)
  d.setHours(0, 0, 0, 0)
  return d
}

function isSameDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  )
}

function formatLongDate(date: Date): string {
  return date.toLocaleDateString('en-GB', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  })
}

interface ContactSalesModalProps {
  isOpen: boolean
  onClose: () => void
  planName?: string
}

export default function ContactSalesModal({
  isOpen,
  onClose,
  planName = 'Enterprise',
}: ContactSalesModalProps) {
  const today = useMemo(() => startOfDay(new Date()), [])
  const [viewMonth, setViewMonth] = useState(
    () => new Date(today.getFullYear(), today.getMonth(), 1),
  )
  const [selectedDate, setSelectedDate] = useState<Date | null>(null)
  const [selectedTime, setSelectedTime] = useState<string | null>(null)
  const [step, setStep] = useState<'schedule' | 'details' | 'success'>('schedule')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const minMonth = new Date(today.getFullYear(), today.getMonth(), 1)
  const canGoPrev = viewMonth > minMonth

  const weeks = useMemo(() => {
    const year = viewMonth.getFullYear()
    const month = viewMonth.getMonth()
    const firstWeekday = new Date(year, month, 1).getDay()
    const daysInMonth = new Date(year, month + 1, 0).getDate()
    const cells: (Date | null)[] = []
    for (let i = 0; i < firstWeekday; i++) cells.push(null)
    for (let day = 1; day <= daysInMonth; day++) cells.push(new Date(year, month, day))
    while (cells.length % 7 !== 0) cells.push(null)
    const rows: (Date | null)[][] = []
    for (let i = 0; i < cells.length; i += 7) rows.push(cells.slice(i, i + 7))
    return rows
  }, [viewMonth])

  const isSelectable = (date: Date): boolean => {
    const weekday = date.getDay()
    return date >= today && weekday !== 0 && weekday !== 6
  }

  const resetAndClose = () => {
    onClose()
    // Reset after the close animation so the form does not flash empty.
    setTimeout(() => {
      setStep('schedule')
      setSelectedDate(null)
      setSelectedTime(null)
      setError(null)
      setSubmitting(false)
      setViewMonth(new Date(today.getFullYear(), today.getMonth(), 1))
    }, 200)
  }

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    if (!selectedDate || !selectedTime) return
    setSubmitting(true)
    setError(null)

    const data = new FormData(e.currentTarget)
    data.append('plan', planName)
    data.append('preferredDate', formatLongDate(selectedDate))
    data.append('preferredTime', `${selectedTime} GMT`)
    data.append('_subject', `Sales call request — ${planName} (${formatLongDate(selectedDate)})`)

    try {
      const res = await fetch(FORMSPREE_ENDPOINT, {
        method: 'POST',
        body: data,
        headers: { Accept: 'application/json' },
      })
      if (!res.ok) throw new Error('Request failed')
      setStep('success')
    } catch {
      setError('Something went wrong. Please try again or email sales@wealthspace.co.uk.')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={resetAndClose}
      maxWidth="md"
      title={step === 'success' ? 'Request received' : `Book a call about ${planName}`}
    >
      {step === 'schedule' && (
        <div className="p-6">
          <p className="text-sm text-slate-500 mb-6">
            Pick a date and time that suits you. Our team will confirm by email.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Calendar */}
            <div>
              <div className="flex items-center justify-between mb-4">
                <button
                  type="button"
                  onClick={() =>
                    canGoPrev &&
                    setViewMonth(
                      new Date(viewMonth.getFullYear(), viewMonth.getMonth() - 1, 1),
                    )
                  }
                  disabled={!canGoPrev}
                  aria-label="Previous month"
                  className="p-1.5 rounded-md text-slate-500 hover:bg-slate-100 disabled:opacity-30 disabled:hover:bg-transparent"
                >
                  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
                  </svg>
                </button>
                <span className="text-sm font-semibold text-slate-900">
                  {MONTH_LABELS[viewMonth.getMonth()]} {viewMonth.getFullYear()}
                </span>
                <button
                  type="button"
                  onClick={() =>
                    setViewMonth(
                      new Date(viewMonth.getFullYear(), viewMonth.getMonth() + 1, 1),
                    )
                  }
                  aria-label="Next month"
                  className="p-1.5 rounded-md text-slate-500 hover:bg-slate-100"
                >
                  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              </div>

              <div className="grid grid-cols-7 gap-1 mb-1">
                {WEEKDAY_LABELS.map((label) => (
                  <span
                    key={label}
                    className="text-center text-[11px] font-semibold uppercase tracking-wide text-slate-400 py-1"
                  >
                    {label.charAt(0)}
                  </span>
                ))}
              </div>

              <div className="grid grid-cols-7 gap-1">
                {weeks.map((row, ri) =>
                  row.map((date, ci) => {
                    if (!date) return <span key={`${ri}-${ci}`} />
                    const selectable = isSelectable(date)
                    const selected = selectedDate && isSameDay(date, selectedDate)
                    return (
                      <button
                        key={`${ri}-${ci}`}
                        type="button"
                        disabled={!selectable}
                        onClick={() => {
                          setSelectedDate(date)
                          setSelectedTime(null)
                        }}
                        className={`aspect-square rounded-lg text-sm font-medium transition-colors ${
                          selected
                            ? 'bg-accent-teal text-white'
                            : selectable
                              ? 'text-slate-700 hover:bg-teal-50 hover:text-accent-teal'
                              : 'text-slate-300 cursor-not-allowed'
                        }`}
                      >
                        {date.getDate()}
                      </button>
                    )
                  }),
                )}
              </div>
            </div>

            {/* Time slots */}
            <div>
              <p className="text-sm font-semibold text-slate-900 mb-4">
                {selectedDate ? formatLongDate(selectedDate) : 'Select a date'}
                {selectedDate && <span className="block text-xs font-normal text-slate-400 mt-0.5">Times shown in GMT</span>}
              </p>
              {selectedDate ? (
                <div className="grid grid-cols-2 gap-2 max-h-[260px] overflow-y-auto pr-1">
                  {TIME_SLOTS.map((time) => {
                    const active = selectedTime === time
                    return (
                      <button
                        key={time}
                        type="button"
                        onClick={() => setSelectedTime(time)}
                        className={`py-2.5 rounded-lg border text-sm font-medium transition-colors ${
                          active
                            ? 'border-accent-teal bg-accent-teal text-white'
                            : 'border-slate-200 text-slate-700 hover:border-accent-teal hover:text-accent-teal'
                        }`}
                      >
                        {time}
                      </button>
                    )
                  })}
                </div>
              ) : (
                <p className="text-sm text-slate-400">
                  Choose a day on the calendar to see available times.
                </p>
              )}
            </div>
          </div>

          <div className="mt-8 flex justify-end">
            <Button
              size="sm"
              disabled={!selectedDate || !selectedTime}
              onClick={() => setStep('details')}
              className={!selectedDate || !selectedTime ? 'opacity-40 cursor-not-allowed' : ''}
            >
              Continue
            </Button>
          </div>
        </div>
      )}

      {step === 'details' && selectedDate && selectedTime && (
        <form className="p-6" onSubmit={handleSubmit}>
          <div className="flex items-center gap-3 bg-teal-50 border border-teal-200 rounded-lg px-4 py-3 mb-6">
            <svg className="w-5 h-5 text-accent-teal flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="3" y="4" width="18" height="18" rx="2" strokeLinecap="round" strokeLinejoin="round" />
              <path strokeLinecap="round" strokeLinejoin="round" d="M16 2v4M8 2v4M3 10h18" />
            </svg>
            <div className="text-sm">
              <span className="font-semibold text-slate-900">{formatLongDate(selectedDate)}</span>
              <span className="text-slate-600"> at {selectedTime} GMT</span>
            </div>
            <button
              type="button"
              onClick={() => setStep('schedule')}
              className="ml-auto text-xs font-semibold text-accent-teal hover:underline"
            >
              Change
            </button>
          </div>

          <div className="space-y-4">
            <Input name="name" label="Full name" placeholder="Jane Adviser" required autoComplete="name" />
            <Input
              name="email"
              type="email"
              label="Work email"
              placeholder="jane@firm.co.uk"
              required
              autoComplete="email"
            />
            <Input name="firm" label="Firm name" placeholder="Your firm" required autoComplete="organization" />
            <Input
              name="advisers"
              label="Number of advisers"
              type="number"
              min={1}
              placeholder="e.g. 8"
            />
            <Textarea
              name="message"
              label="Anything you would like us to know? (optional)"
              placeholder="Tell us a little about your firm and what you are looking for."
              className="min-h-[110px]"
            />
          </div>

          {error && <p className="mt-4 text-sm text-red-500">{error}</p>}

          <div className="mt-6 flex items-center justify-between">
            <button
              type="button"
              onClick={() => setStep('schedule')}
              className="text-sm font-semibold text-slate-500 hover:text-slate-900"
            >
              Back
            </button>
            <Button type="submit" size="sm" disabled={submitting}>
              {submitting ? 'Sending...' : 'Request this time'}
            </Button>
          </div>
        </form>
      )}

      {step === 'success' && (
        <div className="p-8 text-center">
          <div className="mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-full bg-teal-50">
            <svg className="w-7 h-7 text-accent-teal" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h3 className="text-xl font-semibold text-slate-900 mb-2">Thanks, we are on it</h3>
          <p className="text-slate-600 max-w-sm mx-auto">
            We have received your request for{' '}
            {selectedDate && <strong>{formatLongDate(selectedDate)}</strong>}
            {selectedTime && <> at <strong>{selectedTime} GMT</strong></>}. A member of the team
            will email you shortly to confirm.
          </p>
          <div className="mt-6">
            <Button size="sm" variant="secondary" onClick={resetAndClose}>
              Done
            </Button>
          </div>
        </div>
      )}
    </Modal>
  )
}
